import { Injectable } from '@angular/core'

import { Action } from '@ngrx/store'
import { createEffect, Actions, ofType } from '@ngrx/effects'
import { Observable, of } from 'rxjs'
import { map, switchMap, mergeMap, catchError, exhaustMap, tap } from 'rxjs/operators'

import * as ShopOrderActions from '@app/shop/store/actions/shopOrder.actions'

import { ShopOrderService } from '@app/shop/services/shopOrder.service'

import { ToastHelper } from '@app/core/services/toast.service'
import { Router } from '@angular/router'
import { CloseModal } from '@app/modals/store'
import { RedirectService } from '@app/shared'
import { ShopInvoicePayload } from '@app/shop/models/shopOrderAccount.model'


@Injectable()
export class ShopShopOrderEffects {
  getShopOrders$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.GetShopOrders>(ShopOrderActions.ShopOrderActionTypes.GET),
    switchMap(action => {
      return this._ShopOrderService.getAll(action.payload).pipe(
        map(resp => {
          return new ShopOrderActions.GetShopOrdersSuccess(resp.data)
        }),
        catchError(error => {
          this.notify.error(error.message || error)
          return of(new ShopOrderActions.GetShopOrdersError(error))})
      )
    })
  ))

  getCompletedShopOrders$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.GetCompletedShopOrders>(ShopOrderActions.ShopOrderActionTypes.GET_COMPLETED),
    switchMap(action => {
      return this._ShopOrderService.getAll(action.payload).pipe(
        map(resp => {
          return new ShopOrderActions.GetCompletedShopOrdersSuccess(resp.data)
        }),
        catchError(error => of(new ShopOrderActions.GetCompletedShopOrdersError(error)))
      )
    })
  ))

  selectOneShopOrder$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.GetOneShopOrder>(ShopOrderActions.ShopOrderActionTypes.GET_ONE),
    switchMap(action => {
      return this._ShopOrderService.getOne(action.payload).pipe(
        mergeMap(resp => {
          return [new ShopOrderActions.GetOneShopOrderSuccess(resp.data.orders[0])]
        }),
        catchError(error => {
          this.notify.error(error.message || error)
          return of(new ShopOrderActions.GetOneShopOrderError(error))})
      )
    })
  ))

  updateShopOrder$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.ShopOrderUpdate>(ShopOrderActions.ShopOrderActionTypes.UPDATE),
    switchMap(action => {
      return this._ShopOrderService.update(action.payload).pipe(
        mergeMap(resp => {
          this.notify.ok('ShopOrder updated successful!')
          return [
            new ShopOrderActions.ShopOrderUpdateSuccess({ id: resp.data._id, changes: resp.data }),
            // Customer or truck changed (re-populated): the truck's QuickBooks customer link changes with it
            ...(action.payload.populate?._customer || action.payload.populate?._unit ? [new ShopOrderActions.GetShopOrderAccounts({ shopOrderId: resp.data._id })] : [])
          ]
        }),
        catchError(error =>{ 
          this.notify.error(error.message || error)
          return of(new ShopOrderActions.DeleteShopOrderError(error))})
      )
    })
  ))

  createShopOrder$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.CreateShopOrder>(ShopOrderActions.ShopOrderActionTypes.CREATE),
    switchMap(action => {
      return this._ShopOrderService.save(action.payload).pipe(
        map(resp => {
          let userIndex = this.redirectService.getUrlIndex()
          this.router.navigate(['/', userIndex, 'shop', resp?.data?._id])
          this.notify.ok('ShopOrder created successful!')
          return new ShopOrderActions.CreateShopOrderSuccess({
            data: resp.data
          })
        }),
        catchError(error =>{ 
          this.notify.error(error.message || error)
          return of(new ShopOrderActions.CreateShopOrderError(error))})
      )
    })
  ))

  createShopOrderFromExpense$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.CreateShopOrderFromExpense>(ShopOrderActions.ShopOrderActionTypes.CREATE_FROM_EXPENSE),
    switchMap(action => {
      return this._ShopOrderService.createFromExpense(action.payload.expenseId).pipe(
        mergeMap(resp => {
          let userIndex = this.redirectService.getUrlIndex()
          this.router.navigate(['/', userIndex, 'shop', resp?.data?._id])
          this.notify.ok('ShopOrder created successful!')
          return [new CloseModal(), new ShopOrderActions.CreateShopOrderFromExpenseSuccess({
            data: resp.data
          })]
        }),
        catchError(error =>{ 
          this.notify.error(error.message || error)
          return of(new ShopOrderActions.CreateShopOrderFromExpenseError(error))})
      )
    })
  ))

  restoreManyShopOrders$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType<typeof ShopOrderActions.RestoreManyShopOrders.prototype>(ShopOrderActions.ShopOrderActionTypes.RESTORE_MANY),
      mergeMap(action =>
        this._ShopOrderService.restoreMany(action.payload.ids).pipe(
          map(resp => {
            this.notify.ok(resp.message)
            return new ShopOrderActions.RestoreManyShopOrdersSuccess(resp)
          }),
          catchError(error => {
            this.notify.error(error)
            return of(new ShopOrderActions.RestoreManyShopOrdersError(error))
          })
        )
      )
    )
  })

  deleteShopOrder$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.DeleteShopOrder>(ShopOrderActions.ShopOrderActionTypes.DELETE),
    switchMap(action => {
      return this._ShopOrderService.delete(action.payload).pipe(
        map(resp => {
          let userIndex = this.redirectService.getUrlIndex()
          this.router.navigate(['/', userIndex, 'shop'])
          this.notify.ok('ShopOrder deleted successful!')
          return new ShopOrderActions.DeleteShopOrderSuccess(resp.data)
        }),
        catchError(error =>{ 
          this.notify.error(error.message || error)
          return of(new ShopOrderActions.DeleteShopOrderError(error))})
      )
    })
  ))

  getShopOrderBOL$ = createEffect(() => {
    return this.actions$.pipe(
      ofType<ShopOrderActions.DeleteShopOrder>(ShopOrderActions.ShopOrderActionTypes.GENERATE_PDF),
      map(
        (action) =>
          new ShopOrderActions.ApiGeneratePDF({
            order: action.payload.order,
            query: action.payload.query,
          })
      )
    )
  })
  apiGeneratePDF$ = createEffect(() => {
    return this.actions$.pipe(
      ofType<typeof ShopOrderActions.ApiGeneratePDF.prototype>(
        ShopOrderActions.ShopOrderActionTypes.API_GENERATE_PDF
      ),
      switchMap((action) => {
        // console.log('[Effect] Get Contract')
        return this._ShopOrderService
          .getShopOrderPDF(action.payload.order._id, action.payload.query)
          .pipe(
            mergeMap((resp) => [
              new ShopOrderActions.ApiGeneratePDFSuccess(action.payload),
              new ShopOrderActions.BrowserTriggerDownload({
                data: resp,
                name: `ShopOrder # ${action.payload.order.orderNumber}.pdf`,
              }),
            ]),
            catchError((error) =>
              of(
                new ShopOrderActions.ApiGeneratePDFError(error)
              )
            )
          )
      })
    )
  })
  downloadClaimFile$ = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.BrowserTriggerDownload>(
      ShopOrderActions.ShopOrderActionTypes.BROWSER_TRIGGER_DOWNLOAD
    ),
    tap(action => {
      const blob = new Blob([action.payload.data], {
        type: 'application/pdf'
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')

      a.href = url
      a.download = action.payload.name
      document.body.appendChild(a)
      a.click()
      setTimeout(function () {
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }, 100)
    })
  ), { dispatch: false });

  getShopOrderAccounts$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.GetShopOrderAccounts>(ShopOrderActions.ShopOrderActionTypes.GET_ACCOUNTS),
    switchMap(action => {
      return this._ShopOrderService.getAccounts(action.payload.shopOrderId).pipe(
        map(resp => new ShopOrderActions.GetShopOrderAccountsSuccess({
          shopOrderId: action.payload.shopOrderId,
          accounts: resp?.data?.accounts || [],
          quickBooksConnected: !!resp?.data?.quickBooksConnected,
          customer: resp?.data?.customer || null
        })),
        catchError(error => {
          this.notify.error(error.message || error)
          return of(new ShopOrderActions.GetShopOrderAccountsError(error))
        })
      )
    })
  ))

  // exhaustMap: a second click while QuickBooks is still working is ignored, not queued
  createShopOrderInvoice$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.CreateShopOrderInvoice>(ShopOrderActions.ShopOrderActionTypes.CREATE_INVOICE),
    exhaustMap(action => {
      return this._ShopOrderService.createInvoice(action.payload.shopOrderId, action.payload.invoice).pipe(
        mergeMap(resp => {
          this.notifySaved(resp?.data, 'created', action.payload.invoice)
          return [
            new CloseModal('SHOP_INVOICE'),
            new ShopOrderActions.CreateShopOrderInvoiceSuccess({ shopOrderId: action.payload.shopOrderId, account: resp.data.account }),
            // The backend fills the order's invoice number/date when they were empty
            new ShopOrderActions.ShopOrderUpdateSuccess({ id: action.payload.shopOrderId, changes: { accounting: resp.data.accounting } }),
            // The first invoice links (or creates) the customer's QuickBooks customer
            new ShopOrderActions.GetShopOrderAccounts({ shopOrderId: action.payload.shopOrderId })
          ]
        }),
        catchError(error => {
          this.notify.error(error.message || error)
          return of(new ShopOrderActions.CreateShopOrderInvoiceError(error))
        })
      )
    })
  ))

  updateShopOrderInvoice$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.UpdateShopOrderInvoice>(ShopOrderActions.ShopOrderActionTypes.UPDATE_INVOICE),
    exhaustMap(action => {
      return this._ShopOrderService.updateInvoice(action.payload.shopOrderId, action.payload.accountId, action.payload.invoice).pipe(
        mergeMap(resp => {
          this.notifySaved(resp?.data, 'updated', action.payload.invoice)
          return [new CloseModal('SHOP_INVOICE'), new ShopOrderActions.UpdateShopOrderInvoiceSuccess({ account: resp.data.account })]
        }),
        catchError(error => {
          this.notify.error(error.message || error)
          return of(new ShopOrderActions.UpdateShopOrderInvoiceError(error))
        })
      )
    })
  ))

  // Save and Send: the invoice saved even when QuickBooks could not email it (sendError)
  private notifySaved(data: { account?: { docNumber?: string }, sendError?: string }, done: string, invoice: ShopInvoicePayload) {
    const name = data?.account?.docNumber ? `QuickBooks invoice #${data.account.docNumber}` : 'QuickBooks invoice'
    if (data?.sendError) {
      this.notify.error(data.sendError)
    } else if (invoice?.send) {
      this.notify.ok(`${name} ${done} and sent to ${invoice.billEmail}`)
    } else {
      this.notify.ok(`${name} ${done}`)
    }
  }

  deleteShopOrderInvoice$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.DeleteShopOrderInvoice>(ShopOrderActions.ShopOrderActionTypes.DELETE_INVOICE),
    exhaustMap(action => {
      return this._ShopOrderService.deleteInvoice(action.payload.shopOrderId, action.payload.accountId).pipe(
        mergeMap(resp => {
          this.notify.ok('QuickBooks invoice deleted')
          return [
            // Deleting from the invoice modal closes it; from the list there is no modal to close (a no-op)
            new CloseModal('SHOP_INVOICE'),
            new ShopOrderActions.DeleteShopOrderInvoiceSuccess({ accountId: action.payload.accountId }),
            // The order's invoice number/date may have moved to another invoice or been cleared
            new ShopOrderActions.ShopOrderUpdateSuccess({ id: action.payload.shopOrderId, changes: { accounting: resp?.data?.accounting } })
          ]
        }),
        catchError(error => {
          this.notify.error(error.message || error)
          return of(new ShopOrderActions.DeleteShopOrderInvoiceError(error))
        })
      )
    })
  ))

  changeShopQuickBooksCustomer$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<ShopOrderActions.ChangeShopQuickBooksCustomer>(ShopOrderActions.ShopOrderActionTypes.CHANGE_QB_CUSTOMER),
    exhaustMap(action => {
      const { shopOrderId, change } = action.payload
      return this._ShopOrderService.changeQuickBooksCustomer(shopOrderId, change).pipe(
        map(resp => {
          this.notify.ok(change.type === 'link' ? 'QuickBooks customer linked' : 'QuickBooks customer unlinked')
          return new ShopOrderActions.ChangeShopQuickBooksCustomerSuccess({ shopOrderId, customer: resp?.data || null })
        }),
        catchError(error => {
          this.notify.error(error?.message || error)
          return of(new ShopOrderActions.ChangeShopQuickBooksCustomerError(error))
        })
      )
    })
  ))

  constructor(
    private actions$: Actions,
    private notify: ToastHelper,
    private _ShopOrderService: ShopOrderService,
    private router: Router,
    private redirectService: RedirectService
  ) { }
}
